<template>
  <v-row dense data-testid="account-table">
    <v-col cols="12">
      <material-card
      :title="$t('accounts')"
      :elevation="0"
      full-width>
        <DataTable
        :items.sync="allAccounts"
        :loading.sync="accountsLoading"
        :customCells="customCells"
        :actions="tableActions"
        defaultSort="name"
        @create="createAccount"
        @refresh="$emit('refresh-accounts')"
        @download="downloadCSV"
        @ticketBooks="$emit('ticket-books')"
        :headers="headers"
        containerClass="pa-2"
        resize
        :tableHeightCalculationData="{ offset: 367 }"
        @click:row="onRowClick"
        showPointerCursor>
          <template #custom-controls>
            <v-col cols="auto">
              <Icon
              elementId="account-context-menu-activator"
              :small="false"
              icon="mdi-dots-vertical"
              @icon-clicked="openAccountContextMenu"
            />
            <v-menu
            v-model="accountContextMenuOpen"
            activator="#account-context-menu-activator"
            left
            offset-y>
              <v-list>
                <v-list-item @click="downloadCSV" id="account-table-action-download-csv">
                  <v-avatar>
                    <v-icon color="primary">mdi-file-delimited-outline</v-icon>
                  </v-avatar>
                  <v-list-item-title>
                    {{ $t('downloadCSV') }}
                  </v-list-item-title>
                </v-list-item>
                <v-list-item v-if="enableAccountQrDownload" @click="openAccountQrDialog" id="account-table-action-download-csv">
                  <v-avatar>
                    <v-icon color="primary">mdi-download</v-icon>
                  </v-avatar>
                  <v-list-item-title>
                    {{ $t('downloadAccountQrs') }}
                  </v-list-item-title>
                </v-list-item>
                <v-list-item @click="openEudrManager" :disabled="!canManageEudr" id="account-table-action-eudr">
                  <v-avatar>
                    <v-icon color="primary">mdi-leaf</v-icon>
                  </v-avatar>
                  <v-list-item-title>
                    {{ $t('eudrManager') }}
                  </v-list-item-title>
                </v-list-item>
              </v-list>
            </v-menu>
            </v-col>
            <v-col cols="auto">
              <ColumnAdjuster
                containerClass="ml-1 mr-3"
                :propColumnSet.sync="columnSet"
                @new-columns-selected="newColumnsSelected"
              />
            </v-col>
          </template>

          <template #filters>
            <v-container fluid class="my-n4">
              <AccountTagFilter
              @tags-chosen="applyFilter"/>
            </v-container>
          </template>

          <template #name="{ item }">
            <AccountName :accountId="item.accountId" :certificationStatus="item.certificationStatus">
              <span>
                {{item.name}}
                <Icon
                v-if="item.invalidContactEmail"
                icon="mdi-email-alert-outline"
                iconColor="error"
                :tooltipText="$t('invalidContactEmail')"/>
              </span>
            </AccountName>
          </template>

          <template #account-tags="{item}">
            <v-container id="accountTagChipsContainer" v-if="item.accountTags.length > 0">
              <v-row justify="center">
                <v-chip-group column>
                  <v-chip
                  small
                  v-for="tag in item.accountTags.slice(0, 3)" :key="`${item.accountId}-${tag}`"
                  :ripple="false" >
                    {{ tag }}
                  </v-chip>
                  <Icon
                v-if="item.accountTags.length > 3"
                  icon="mdi-dots-horizontal"
                  iconColor="black"
                  :tooltipText="item.accountTags.slice(3, item.accountTags.length).join(', ')"/>
                </v-chip-group>
              </v-row>
            </v-container>
            <span v-else>
              {{$t('notAvailable')}}
            </span>
          </template>

          <template #insurance-expiry-date="{ item }">
            <Icon icon="mdi-shield-alert" margin="" v-if="showExpiredInsuranceWarning(item)"/>
            <span v-if="item.insuranceExpiryDate">
              {{ companyOffsetToLocalDate(item.insuranceExpiryDate, 'MM/DD/YYYY') }}
            </span>
            <span v-else>
              {{$t('notAvailable')}}
            </span>
          </template>

          <template #related="{ item }">
            <td @click.stop.prevent class="text-center cursor-default">
              <Icon
              dataTestId="account-detail-button"
              icon="mdi-information-outline"
              iconColor="primary"
              :small="false"
              :tooltipText="$t('details')"
              @icon-clicked="viewAccountDetails(item)"/>
              <Icon
              dataTestId="account-isexternal"
              :icon="item.isExternal ? 'mdi-domain' : 'mdi-home'"
              :iconColor="item.isExternal ? 'grey' : 'black'"
              :small="false"
              :tooltipText="item.isExternal ? $t('external') : $t('internal')"
              />
              <Icon v-if="item.penaltyDefinitionId"
              icon="weight-alert"
              :small="false"
              :tooltipText="$t('penalty')"
              />
            </td>
          </template>

          <template v-slot:actions="{ item }">
            <td @click.stop.prevent class="text-center cursor-default">
              <Icon v-for="(aa, index) in accountActions" :key="`${aa.name}-${index}`"
              :icon="aa.icon"
              :iconColor="aa.color"
              :dataTestId="aa.testId"
              :tooltipText="$t(aa.name)"
              @icon-clicked="aa.action(item)"
              :disabled="aa.disabled"
              :elementId="`account-table-action-${kebabCase(aa.name)}`"/>
            </td>
          </template>
        </DataTable>
      </material-card>
    </v-col>
  </v-row>
</template>

<script>
import { mapGetters, mapActions, mapMutations } from 'vuex'
import moment from 'moment'
import { saveAs } from 'file-saver'
import { accountHeaders } from '@/headers/Account'
import { companyOffsetToLocalDate, utcToLocalDate } from '@/utils/DateFormatter.js'
import { generateCsvString } from '@/utils/CSVUtility'
import { UserClaims } from '@/utils/Enumerations'
import { userAssignedClaim } from '@/utils/ClaimUtility'
import { kebabCase } from 'lodash'

export default {
  name: 'AccountTable',

  components: {
    DataTable: () => import('@/components/core/table/DataTable.vue'),
    Icon: () => import('@/components/helper/Icon.vue'),
    AccountTagFilter: () => import('@/components/account/AccountTagFilter.vue'),
    ColumnAdjuster: () => import('@/components/core/ColumnAdjuster.vue'),
    AccountName: () => import('@/components/account/AccountName.vue')
  },

  data: () => ({
    columnSet: null,
    accountContextMenuOpen: false
  }),

  computed: {
    ...mapGetters('account', ['allAccounts', 'accountsLoading']),

    headers () { return this.columnSet?.getColumns() ?? [] },

    canManageEudr () { return userAssignedClaim(UserClaims.AccountManager) },

    tableActions () {
      return [
        {
          actionName: 'ticketBooks',
          icon: 'mdi-bookshelf',
          text: this.$t('ticketBooks'),
          id: 'account-table-action-ticket-books'
        },
        {
          actionName: 'refresh',
          icon: 'mdi-refresh',
          text: this.$t('refresh'),
          id: 'account-table-action-refresh'
        },
        {
          actionName: 'create',
          icon: 'mdi-plus',
          text: this.$t('new'),
          disabled: !userAssignedClaim(UserClaims.AccountManager),
          id: 'account-table-action-create'
        }
      ]
    },

    customCells () {
      return [
        {
          slotName: 'name',
          value: 'name'
        },
        {
          slotName: 'account-tags',
          value: 'accountTags'
        },
        {
          slotName: 'insurance-expiry-date',
          value: 'insuranceExpiryDate'
        },
        {
          slotName: 'certification-expiry-date',
          value: 'certificationExpiryDate',
          dateFormat: 'MM/DD/YYYY'
        },
        {
          slotName: 'actions',
          value: 'actions'
        },
        {
          slotName: 'related',
          value: 'related'
        }
      ]
    },

    accountActions () {
      return [
        {
          name: 'edit',
          icon: 'mdi-pencil',
          color: 'success',
          testId: 'account-edit-button',
          action: this.editAccount,
          disabled: !userAssignedClaim(UserClaims.AccountManager)
        },
        {
          name: 'delete',
          icon: 'mdi-delete-forever',
          color: 'error',
          testId: 'account-delete-button',
          action: this.deleteAccount,
          disabled: !userAssignedClaim(UserClaims.AccountManager)
        }
      ]
    },

    enableAccountQrDownload () {
      return this.allAccounts.some(a => a.code && a.isExternal)
    }
  },

  created () {
    this.columnSet = accountHeaders()
  },

  methods: {
    ...mapActions('account', ['fetchAccounts']),
    ...mapMutations('payable', ['setFocusedAccount']),
    generateCsvString,
    kebabCase,
    companyOffsetToLocalDate,

    openAccountContextMenu () {
      if (document.getElementById('account-context-menu-activator')) {
        this.accountContextMenuOpen = true
      }
    },

    editAccount (account) {
      this.$emit('account-action', {
        actionType: 'edit-account',
        account: account
      })
    },

    createAccount () {
      this.$emit('account-action', {
        actionType: 'create-account'
      })
    },

    viewAccountDetails (account) {
      this.$emit('account-action', {
        actionType: 'view-account-details',
        account: account
      })
    },

    deleteAccount (account) {
      this.$emit('account-action', {
        actionType: 'delete-account',
        account: account
      })
    },

    applyFilter (tags) {
      this.$emit('tags-chosen', tags)
    },

    downloadCSV () {
      const csvString = this.generateCsvString(this.allAccounts, [
        { label: 'name', value: a => a.name },
        { label: 'insuranceExpiryDate', value: a => a.insuranceExpiryDate ? utcToLocalDate(a.insuranceExpiryDate) : undefined },
        { label: 'certificationExpiryDate', value: a => a.certificationExpiryDate ? utcToLocalDate(a.certificationExpiryDate) : undefined },
        { label: 'externalOrInternal', value: a => a.isExternal ? 'External' : 'Internal' },
        { label: 'tags', value: a => (a.accountTags ?? []).join(', ') },
        { label: 'code', value: a => a.code },
        { label: 'exportCode', value: a => a.exportCode }
      ], { emptyValue: this.$t('notAvailable') })
      const blob = new Blob([csvString], { type: 'text/plain;charset=utf-8' })
      saveAs(blob, `Accounts-${moment().format()}.csv`)
    },

    openAccountQrDialog () {
      this.$emit('account-action', {
        actionType: 'open-account-qr-download-dialog'
      })
    },

    openEudrManager () {
      this.accountContextMenuOpen = false
      this.$emit('account-action', {
        actionType: 'open-eudr-manager'
      })
    },

    newColumnsSelected (newColumns) {
      this.columnSet.columns = newColumns.columns
    },

    showExpiredInsuranceWarning (item) {
      if (item.insuranceExpiryDate == null) return false
      const insuranceExpiryDateAsLocal = companyOffsetToLocalDate(item.insuranceExpiryDate, 'MM/DD/YYYY')
      const insuranceIsExpired = moment(insuranceExpiryDateAsLocal)
        .isSameOrBefore(moment().endOf('day'))
      return insuranceIsExpired
    },

    onRowClick ({ data }) {
      this.viewAccountDetails(data)
    }
  }
}
</script>

<style>
#accountTagChipsContainer .v-slide-group__content {
  display: block;
}
</style>
